import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanEditor = ({ data, onChange, token }) => {
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      onChange({ ...data, floorPlanImage: imageUrl });
      toast.success('Floor plan image uploaded successfully!');
    } catch (error) {
      console.error('Plan upload error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 space-y-6">
      <h2 className="text-2xl font-bold text-slate-900">Plan Section</h2>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Section Title</label>
        <Input
          value={data.title}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
          placeholder="Property Plans"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
        <Textarea
          value={data.description}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          rows={3}
          placeholder="Detailed layouts and specifications"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Note (for floor plan requests)</label>
        <Textarea
          value={data.note}
          onChange={(e) => onChange({ ...data, note: e.target.value })}
          rows={2}
          placeholder="Floor plans available upon request"
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Floor Plan Image</h3>
        <p className="text-sm text-slate-600 mb-4">
          Upload an image of your floor plans to display instead of the placeholder
        </p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Image URL or Upload</label>
          <div className="flex items-center gap-4">
            <Input 
              value={data.floorPlanImage || ''} 
              onChange={(e) => onChange({ ...data, floorPlanImage: e.target.value })} 
              placeholder="https://... or upload below"
              className="flex-1"
            />
            <label htmlFor="floorplan-image-upload">
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => document.getElementById('floorplan-image-upload').click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </label>
            <input
              id="floorplan-image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          {data.floorPlanImage && (
            <div className="mt-4">
              <p className="text-sm text-slate-600 mb-2">Preview:</p>
              <img 
                src={data.floorPlanImage} 
                alt="Floor plan preview" 
                className="w-full h-auto max-h-96 object-contain rounded-lg border border-slate-200 bg-white" 
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanEditor;
